/*
* Indicator component specific for Adalia M&E.
* 
* This component expects a project-id context. Rendering without causes an error.
* 
*/
import React, { useEffect, useContext, useState } from 'react';

import { FaIcon } from '../../common/Icons.jsx'

import { FormContext } from '../form_context.jsx';
import { cachedFetchJson } from '../../../helpers/fetch.js';

import Base from './base.jsx'; 
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import {
  Mode,
  DataType,
  castDataType
} from '../common.jsx';

import Dialog, {DialogFooter, DialogHeader} from '../../bootstrap/Dialog.jsx';
import ValueGrid from '../../indicator_value/ValueGrid.lazy.jsx'
import LoadingIndicator from "../../common/loading_indicator.jsx";
import { Portal } from "../../common/Portal.jsx";
import FormattedNumber from "../../common/FormattedNumber.jsx";
import {Table, ColumnFilter } from "../../common/Table/index.jsx";

import {
  result_framework_list_common_indicators_for_programme_path,
  indicator_value_value_set_options_path,
  monitoring_indicator_value_show_all_path
} from '~/routes';

window.rfCache = {};


const getCommonIndicators = (defaultOpt) => {
  return new Promise((resolve, reject) => { 
    cachedFetchJson(result_framework_list_common_indicators_for_programme_path({ is_aggregate: false, version: 'any'})).then((res) => {
      if (res){
        resolve({
          options: [defaultOpt, ...res],
          fetchingOptions: false
        })
      } else {
        reject("Invalid response")
      }
    })
  })
}

const loadIndicatorOptions = (defaultOpt = {'value': 0, 'label': I18n.t('select_blank_option_default', {scope: 'fe'})}) => {
  return getCommonIndicators(defaultOpt);
}

const STATUS = {
  SUBMITTED: "submitted",
  APPROVED: "approved",
  PENDING_APPROVAL: "pending_approval",
  REJECTED: "rejected",
  PENDING_REJECT: "pending_reject",
};

const DISABLED_STATUSES = [STATUS.APPROVED, STATUS.SUBMITTED, STATUS.PENDING_REJECT, STATUS.PENDING_APPROVAL];

const DISAGGREGATION_TYPE = {
  STRICT: "strict",
  LAX: "lax",
}

const IndicatorValuePickerDialog = ({
  indicator,
  programmeId,
  onRowSelection,
  indicatorValueData,
  initialSelectedRows,
  showValuesLink,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState(initialSelectedRows || []);
  const tableContainerRef = React.useRef();

  const updateSelectedRows = (rows) => {
    if (JSON.stringify(selectedRows.sort()) !== JSON.stringify(rows.sort())) {
      setSelectedRows(rows);
    }
  };

  const handleRowSelection = (e) => {
    e.preventDefault();
    onRowSelection(selectedRows);
    setShowDialog(false);
  };

  const resize = () => {
    if (tableContainerRef.current) {
      const container = jQuery(tableContainerRef.current);
      const containerFluid = container.closest(".container-fluid");
      // Full height - 100 px for dialog footer
      container.css(
        "height",
        "calc(100vh - " + (container[0].getBoundingClientRect().top + 100) + "px)"
      );
      container.width(containerFluid.width());
    }
  };

  useEffect(() => {
    resize();

    jQuery(window).on("resize", resize);
    jQuery(window).on("click", resize);

    return () => {
      jQuery(window).off("resize", resize);
      jQuery(window).off("click", resize);
    };
  });

  const title = `${I18n.t('select_values_for', {scope: 'fe'})}${indicator.label}`;

  const getRowId = React.useCallback((row) => {
    return row.id;
  }, []);

  const onDialogClose = () => {
    setShowDialog(false);
  };

  return (
    <div>
      {showDialog && (
        <Portal
          component={Dialog}
          size="fw"
          footer={<DialogFooter onClose={onDialogClose} onAdd={handleRowSelection} />}
          header={<DialogHeader onClose={onDialogClose} title={title} />}
          onClose={onDialogClose}
        >
          {
            <div className="container-fluid">
              <div ref={tableContainerRef} className="react-table-container non-full-width">
                <ValueGrid
                  projectId={programmeId}
                  values={indicatorValueData.values}
                  disaggregations={indicatorValueData.disaggregations}
                  additionalFields={indicatorValueData.additional_fields}
                  unit={indicatorValueData.unit_name}
                  valueOptions={indicatorValueData.value_options}
                  maySelectApprovedValues={indicatorValueData.may_select_approved_values}
                  afterSave={() => {}}
                  hiddenAdditionalFields={[]}
                  disableEdit={true}
                  setSelectedRows={updateSelectedRows}
                  initialSelectedRowIds={Object.fromEntries(
                    initialSelectedRows.map((r) => [r.id.toString(), true])
                  )}
                  alwaysSelectedRowIds={initialSelectedRows
                    .filter((r) => r.status == STATUS.PENDING_APPROVAL)
                    .map((r) => r.id)}
                  additionalTableProps={{ getRowId: getRowId }}
                  disableSelectionForStatuses={DISABLED_STATUSES}
                />
              </div>
            </div>
          }
        </Portal>
      )}
      <button
        className="btn btn-primary mr-1"
        onMouseDown={(e) => {
          e.preventDefault();
          setShowDialog(!showDialog);
        }}
      >
        <FaIcon type="check-circle" className="mr-1" />
        {I18n.t("select_values", { scope: "fe" })}
      </button>
      {showValuesLink}
    </div>
  );
};

const SummaryTable = ({ disaggregations, disaggregationType, values }) => {
  const tableContainerRef = React.useRef();
  const [wrapperHeight, setWrapperHeight] = React.useState(40);
  const isLaxDisaggregation = disaggregationType == DISAGGREGATION_TYPE.LAX;
  const total = React.useMemo(() =>  values.reduce((sum, row) => (parseFloat(row.value, 10) || 0) + sum, 0), [values]);

  const data = React.useMemo(
    () => disaggregations.flatMap((d) => {
        const categories = d.categories.map((c) => ({
          dimension: d.dimension,
          category: c.label,
          value: isLaxDisaggregation
            ? values
                .map((v) => v.additional_information[`lax_disagg:${c.value}`])
                .reduce((sum, value) => (parseFloat(value, 10) || 0) + sum, 0)
            : values
                .filter((v) => v.disaggregation_category_ids.includes(c.value))
                .reduce((sum, row) => (parseFloat(row.value, 10) || 0) + sum, 0),
        }));

        return [
          ...categories,
          ...(isLaxDisaggregation
            ? [
                {
                  dimension: d.dimension,
                  category: I18n.t("unknown"),
                  value:
                    total - categories.reduce((sum, c) => (parseFloat(c.value, 10) || 0) + sum, 0),
                },
              ]
            : []),
        ];
      }),
    [values, disaggregations, isLaxDisaggregation, total]
  );

  const columns = React.useMemo(
    () =>
      [
        disaggregations.length > 0
          ? {
              Header: I18n.t("dimension"),
              accessor: "dimension",
              filter: "includesOption",
              widthOffset: 10,
              Filter: (props) => <ColumnFilter autoDetectOptions {...props} />,
            }
          : null,
        disaggregations.length > 0
          ? {
              Header: I18n.t("category"),
              accessor: "category",
              filter: "includesOption",
              widthOffset: 10,
              Filter: (props) => <ColumnFilter autoDetectOptions {...props} />,
            }
          : null,
        {
          Header: I18n.t("value"),
          accessor: "value",
          widthOffset: 10,
          Footer: () => (
            <>
              <FormattedNumber
                number={total}
              />{" "}
              {`(${I18n.t("total")})`}
            </>
          ),
        },
      ].filter((c) => c),
    [values, disaggregations, total]
  );

  const handleResize = (height) => {
    setWrapperHeight(height);
  }

  return (
    <div ref={tableContainerRef} className="react-table-container non-full-width" style={{height: wrapperHeight}}>
      <Table
        data={data}
        columns={columns}
        isFixedSize
        onClientHeightChange={handleResize}
        showFooter
        itemDefaultSize={40}
      />
    </div>
  );
};


export default class AdaliaMeIndicatorValueSet extends Base {
  static contextType = FormContext;
  
  constructor(props) {
    super(props);
    this.state = {
      options: [],
      fetchingOptions: true,
      fetchingIndicatorData: true,
      indicatorValueData: null
    }
  }

  static get CATEGORY() {
    return 'question';
  }
  
  static get TYPE() {
    return 'adalia_me_indicator_value_set';
  }
  
  static icon(){
    return <i className="fa fa-table"></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
        label: 'field_default_label',
        description: 'field_default_description',
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      indicator: {},
      label: 'label',
      description: 'description',
      name: 'field_' + Helpers.randomString(),
      prettyView: {
          hide: false,
          show_label: false
      }
    });
  }

  fetchIndicatorValueData = () => {
    let routeParams = {
      // projectId in a report context, programmeId when editing the form
      project_id: this.context.projectId || this.context.programmeId,
      programme_id: this.context.programmeId,
      indicator_id: this.props.content.indicator,
    };
    if ([Mode.FILL, Mode.VIEW].includes(this.props.mode)) {
      routeParams.common = true
    }

    jQuery
      .get(
        indicator_value_value_set_options_path(routeParams)
      )
      .done((data) => {
        this.setState({indicatorValueData: { ...data.indicator }, fundIndicatorId: data.fund_indicator_id, fetchingIndicatorData: false});
      });
  };

  renderView() {
    const selectedIds = (this.getAnswer() || []).map((r) => r.id);
    const selectedRows = ((this.state.indicatorValueData || {}).values || []).filter((v) => selectedIds.includes(v.id));
    let valueLinkParams = {
      project_id: this.context.programmeId,
      indicator_id: this.state.fundIndicatorId,
    };
    const cfParam = new URL(window.location).searchParams.get("cf");

    if (cfParam) {
      // Open indicator values in the correct fund's context
      valueLinkParams.programme_identifier = cfParam;
    }

    const showValuesLink =
      !this.state.fetchingIndicatorData && this.state.fundIndicatorId ? (
        <a
          href={monitoring_indicator_value_show_all_path(valueLinkParams)}
          className="btn btn-primary"
        >
          {I18n.t("show_values", { scope: "fe" })}
          <FaIcon type="list" className="ml-1" />
        </a>
      ) : null;

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label className={this.props.content.label_hidden ? 'hidden-label': ''}><span className="field-label">{this.getText('label')}</span></label>
        {this.descriptionText()}
        {this.renderSelectedValues(selectedRows)}
        <input type="hidden" defaultValue={this.getRawAnswer()} name={this.fullFieldName()} />
        <br />

        {this.props.mode == Mode.VIEW && !this.props.pdf ? (
          <div>{showValuesLink}</div>
        ) : null}

        {this.props.mode == Mode.FILL && !this.state.fetchingIndicatorData && (
          <IndicatorValuePickerDialog
            programmeId={this.context.programmeId}
            indicator={this.props.content.indicator}
            onRowSelection={this.onRowSelection}
            indicatorValueData={this.state.indicatorValueData}
            initialSelectedRows={selectedRows}
            showValuesLink={showValuesLink}
          />
        )}
      </div>
    );
  }

  renderPrettyView() {
    const selectedIds = (this.getAnswer() || []).map((r) => r.id);
    const selectedRows = ((this.state.indicatorValueData || {}).values || []).filter((v) => selectedIds.includes(v.id));
    let valueLinkParams = {
      project_id: this.context.programmeId,
      indicator_id: this.state.fundIndicatorId,
    };
    const cfParam = new URL(window.location).searchParams.get("cf");

    if (cfParam) {
      // Open indicator values in the correct fund's context
      valueLinkParams.programme_identifier = cfParam;
    }

    const showValuesLink =
      !this.state.fetchingIndicatorData && this.state.fundIndicatorId ? (
        <a
          href={monitoring_indicator_value_show_all_path(valueLinkParams)}
          className="btn btn-primary"
        >
          {I18n.t("show_values", { scope: "fe" })}
          <FaIcon type="list" className="ml-1" />
        </a>
      ) : null;

    const { hide = false, show_label = false } = this.props.content.prettyView || {};
    return (hide ? null :
      <>
        {show_label &&
          <label>
              <span className="field-label">{this.getText('label')}</span>
          </label>
        }
        <div style={{ display: "flex", flexDirection: "column" }}>
          {this.renderSelectedValues(selectedRows)}
          <input type="hidden" defaultValue={this.getRawAnswer()} name={this.fullFieldName()} />
          <br />

          {this.props.mode == Mode.VIEW && !this.props.pdf ? (
            <div>{showValuesLink}</div>
          ) : null}
        </div>
      </>
    );
  }

  renderSelectedValues = (selectedRows) => {    
      return this.state.fetchingIndicatorData ? (
        <>
          <LoadingIndicator
            size="sm"
            additionalInfo={
              !this.props.content.indicator.id ? I18n.t("indicator_value_set_error") : ""
            }
          />
        </>
      ) : selectedRows.length > 0 ? (
        <div className="col-lg-12">
          <SummaryTable
            values={selectedRows}
            disaggregations={this.state.indicatorValueData.disaggregations}
            disaggregationType={this.state.indicatorValueData.disaggregation_type}
          />
        </div>
      ) : (
        <em>{I18n.t("no_data")}</em>
      );
  }

  formatSelectedRows = (selectedRows) => {
    let fieldsOfInterest = (
      {id, indicator_id, value, description, value_date, project_id, additional_information, formatted_value_date, formatted_value, disaggregation_category_ids}
    )  => (
      {id, indicator_id, value, description, value_date, project_id, additional_information, formatted_value_date, formatted_value, disaggregation_category_ids}
    );

    return selectedRows.map(r => fieldsOfInterest(r))
  }

  getAnswer() {
    return this.getRawAnswer() ? JSON.parse(this.getRawAnswer()) : [];
  }

  
  onRowSelection = (selectedRows) => {
    // Store relevant fields from the values as JSON
    this.props.updateAnswerCallback(this.fullFieldName(), JSON.stringify(this.formatSelectedRows(selectedRows)));
  }

  
  
  updateIndicator = (event) => {
    let content = {id: this.props.content.id, translations: JSON.parse(JSON.stringify(this.props.content.translations)) || {}};
    let selectedOption = this.state.options.find(o => o.value == event.target.value);
    content.indicator = {id: selectedOption.value, label: selectedOption.label};
    this.props.updateSettingsCallback(content.id, content, true);
  }
  
  renderSettings() {
    let options = ((this.state.options).map(o => [o.value, o.label]) || [])

    return <div>
      { !this.state.fetchingOptions ? 
          <Setting.Dropdown 
          name="indicator" 
          label={I18n.t('indicator_label', {scope: 'fe'})} 
          value={this.props.content.indicator.id} 
          onChange={this.updateIndicator} 
          options={options}
        /> 
      : 
        <input type="text" disabled={true} className='form-control' value={I18n.t('loading')}/>
      }
      <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      {this.renderPrettyViewSettings()}
    </div>
  }

  beforeUpdateSettings(content) {
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    this.beforeUpdateSettingsPrettyView(content);
  }
    
  componentDidMount() {
    if ([Mode.SETTINGS].includes(this.props.mode)) { 
      if (this.state.options.length == 0) {
        this.handleIndicatorOptionLoad();
      }
    }
    if ([Mode.FILL, Mode.VIEW, Mode.EDIT].includes(this.props.mode)) { 
      if (this.props.content.indicator.id) {
        this.fetchIndicatorValueData();
      }
    }

    super.componentDidMount();

  }

  componentDidUpdate(prevProps, prevState) {
    if ([Mode.FILL, Mode.VIEW, Mode.EDIT].includes(this.props.mode)) {       
      if (this.props.content && (this.props.content.indicator.id != prevProps.content.indicator.id)) {
        this.setState({fetchingIndicatorData: true})
        this.fetchIndicatorValueData();
      }
    }    
    super.componentDidUpdate();
  }
  
  handleIndicatorOptionLoad = () => {
    loadIndicatorOptions().then(res => {
      this.setState(res)
    }).catch(e => {
      alert(e);
    })
  }
}


